/**
 * Adjuntos de una tarea: cabecera con contador y «Agregar», zona para arrastrar archivos, grilla de
 * miniaturas para las imágenes y lista de documentos. Tocar una miniatura o un documento abre el visor
 * a pantalla completa (`VisorAdjuntos`), que pasa entre todos los adjuntos.
 *
 * Las validaciones de aquí (tipo, tamaño, cupo) solo ahorran un viaje al servidor; la API vuelve a
 * comprobarlo todo y manda. Los botones de subir y quitar solo se ocultan según el rol: el permiso
 * real lo decide la API.
 */
import { useEffect, useRef, useState, type DragEvent, type MouseEvent } from 'react';
import {
  MAXIMO_ADJUNTOS_POR_TAREA,
  TAMANO_MAXIMO_ADJUNTO,
  type Adjunto,
} from '@cdtec-backlog/contrato';
import { Esqueleto } from '../../componentes/Esqueleto';
import { Icono } from '../../componentes/Icono';
import { useMostrarTrasRetardo } from '../../componentes/useMostrarTrasRetardo';
import { formatearTamano } from '../../lib/formato';
import { ACEPTAR, mensajeDeErrorAdjunto, tipoDeArchivo, urlAdjunto, validarArchivo } from './api';
import { useAdjuntos, useEliminarAdjunto, useSubirAdjunto } from './hooks';
import { IconoExtension } from './IconoExtension';
import { MetaAdjunto } from './MetaAdjunto';
import { ordenarParaVisor, tipoDePrevisualizacion } from './previsualizacion';
import { VisorAdjuntos } from './VisorAdjuntos';
import './ListaAdjuntos.css';

interface Props {
  tareaId: number;
  /** Id de la persona con sesión, para saber cuáles adjuntos son suyos. */
  yoId: number;
  puedeEscribir: boolean;
  puedeAdministrar: boolean;
}

interface Subida {
  clave: string;
  nombre: string;
  /** Fracción enviada, de 0 a 1 (la informa `subirArchivo`). */
  fraccion: number;
  estado: 'cola' | 'subiendo' | 'listo' | 'error';
}

/** Cuánto se queda a la vista una tanda de subidas ya terminada. */
export const SUBIDA_LISTA_MS = 1500;

/** Un archivo de la tanda: nombre, porcentaje (o «Listo») y la barra de progreso real. */
function FilaSubida({ subida }: { subida: Subida }) {
  const porcentaje = Math.round(subida.fraccion * 100);
  const estado =
    subida.estado === 'listo' ? (
      <>
        <Icono nombre="check" tamano={14} />
        Listo
      </>
    ) : subida.estado === 'error' ? (
      'No se pudo subir'
    ) : subida.estado === 'cola' ? (
      'En espera'
    ) : (
      `${porcentaje}%`
    );
  return (
    <li className={`subida subida--${subida.estado}`}>
      <span className="subida__fila">
        <span className="subida__nombre" title={subida.nombre}>
          {subida.nombre}
        </span>
        <b className="subida__estado">{estado}</b>
      </span>
      <progress
        className="subida__barra"
        value={subida.estado === 'error' ? 0 : subida.fraccion}
        max={1}
        aria-label={`Progreso de ${subida.nombre}`}
      />
    </li>
  );
}

/** Cuánto espera el «¿Quitar?» un segundo clic antes de volver a la papelera. */
export const ESPERA_CONFIRMAR_MS = 2500;

const esImagen = (adjunto: Adjunto) => tipoDePrevisualizacion(adjunto) === 'imagen';

/** Tres miniaturas fantasma con la grilla de las imágenes (4:3), mientras se trae la lista. */
export function MiniaturasCargando({ mostrar = true }: { mostrar?: boolean }) {
  return (
    <div className={`adjuntos__fotos${mostrar ? '' : ' carga--espera'}`} aria-hidden="true">
      <Esqueleto forma="miniatura" />
      <Esqueleto forma="miniatura" />
      <Esqueleto forma="miniatura" />
    </div>
  );
}

export function ListaAdjuntos({ tareaId, yoId, puedeEscribir, puedeAdministrar }: Props) {
  const consulta = useAdjuntos(tareaId);
  const mostrarEsqueleto = useMostrarTrasRetardo(consulta.isPending);
  const subir = useSubirAdjunto(tareaId);
  const eliminar = useEliminarAdjunto(tareaId);
  const entrada = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [subidas, setSubidas] = useState<Subida[]>([]);
  const [problemas, setProblemas] = useState<string[]>([]);
  // El visor se guarda por id (no por posición): el refresco de 30 s no lo cierra ni lo mueve.
  const [abiertoId, setAbiertoId] = useState<number | null>(null);
  // Al terminar la tanda, las filas («✓ Listo») se quedan un momento y dejan el lugar a las miniaturas.
  const terminada =
    subidas.length > 0 && subidas.every((s) => s.estado === 'listo' || s.estado === 'error');
  useEffect(() => {
    if (!terminada) return;
    const temporizador = window.setTimeout(() => setSubidas([]), SUBIDA_LISTA_MS);
    return () => window.clearTimeout(temporizador);
  }, [terminada]);

  async function subirArchivos(lista: FileList | File[]) {
    const archivos = Array.from(lista);
    if (archivos.length === 0 || !consulta.data) return;
    const nuevosProblemas: string[] = [];
    // Cuántos caben todavía: se descuenta a medida que se aceptan archivos de esta tanda.
    let cupo = consulta.data.disponibles;
    const aceptados: File[] = [];
    for (const archivo of archivos) {
      const motivo = validarArchivo(archivo, cupo);
      if (motivo) nuevosProblemas.push(motivo);
      else {
        aceptados.push(archivo);
        cupo -= 1;
      }
    }
    setProblemas(nuevosProblemas);
    const tanda = Date.now();
    setSubidas(
      aceptados.map((archivo, i) => ({
        clave: `${tanda}-${i}`,
        nombre: archivo.name,
        fraccion: 0,
        estado: 'cola',
      })),
    );
    const cambiar = (i: number, cambios: Partial<Subida>) =>
      setSubidas((actuales) => actuales.map((s, j) => (j === i ? { ...s, ...cambios } : s)));
    // Una a una: así el cupo y los mensajes de error se corresponden con cada archivo.
    for (const [i, archivo] of aceptados.entries()) {
      cambiar(i, { estado: 'subiendo' });
      try {
        await subir.mutateAsync({
          archivo,
          tipo: tipoDeArchivo(archivo) ?? archivo.type,
          alProgresar: (fraccion) => cambiar(i, { fraccion }),
        });
        cambiar(i, { estado: 'listo', fraccion: 1 });
      } catch (error) {
        cambiar(i, { estado: 'error' });
        nuevosProblemas.push(`«${archivo.name}»: ${mensajeDeErrorAdjunto(error)}`);
        setProblemas([...nuevosProblemas]);
      }
    }
  }

  function alSoltar(evento: DragEvent<HTMLElement>) {
    evento.preventDefault();
    setArrastrando(false);
    if (puedeEscribir && !subiendo) void subirArchivos(evento.dataTransfer.files);
  }

  function quitar(adjunto: Adjunto) {
    eliminar.mutate(adjunto.id, {
      onError: (error) => setProblemas([`«${adjunto.nombre}»: ${mensajeDeErrorAdjunto(error)}`]),
    });
  }

  const items = consulta.data?.items ?? [];
  const disponibles = consulta.data?.disponibles ?? 0;
  const pendientes = subidas.filter((s) => s.estado === 'cola' || s.estado === 'subiendo');
  const subiendo = pendientes.length > 0;
  const orden = ordenarParaVisor(items);
  const imagenes = orden.filter(esImagen);
  const documentos = orden.filter((a) => !esImagen(a));
  const puedeQuitar = (adjunto: Adjunto) =>
    puedeEscribir && (puedeAdministrar || adjunto.subidoPor?.id === yoId);
  const elegir = () => entrada.current?.click();
  const cupoTexto = `${items.length} de ${MAXIMO_ADJUNTOS_POR_TAREA}`;

  return (
    <section
      className="adjuntos"
      aria-labelledby={`adjuntos-titulo-${tareaId}`}
      onDragOver={(evento) => {
        if (!puedeEscribir) return;
        evento.preventDefault();
        setArrastrando(true);
      }}
      onDragLeave={(evento) => {
        if (!evento.currentTarget.contains(evento.relatedTarget as Node | null)) {
          setArrastrando(false);
        }
      }}
      onDrop={alSoltar}
    >
      <div className="adjuntos__cabecera">
        <h3 id={`adjuntos-titulo-${tareaId}`} className="seccion-titulo adjuntos__titulo">
          Adjuntos
        </h3>
        <span
          className="adjuntos__cuenta"
          title={`${cupoTexto} adjuntos permitidos`}
          aria-label={`${cupoTexto} adjuntos permitidos`}
        >
          {items.length}
        </span>
        {puedeEscribir && (
          <>
            <input
              ref={entrada}
              type="file"
              multiple
              accept={ACEPTAR}
              className="solo-lectores"
              tabIndex={-1}
              aria-label="Elegir archivos para adjuntar"
              onChange={(evento) => {
                const elegidos = Array.from(evento.target.files ?? []);
                evento.target.value = '';
                void subirArchivos(elegidos);
              }}
            />
            <button
              type="button"
              className="adjuntos__agregar"
              disabled={subiendo || !consulta.data || disponibles <= 0}
              onClick={elegir}
            >
              <Icono nombre="clip" tamano={14} />
              Agregar
            </button>
          </>
        )}
      </div>

      {puedeEscribir && (
        <>
          <div className={`adjuntos__zona${arrastrando ? ' adjuntos__zona--sobre' : ''}`}>
            Arrastra archivos aquí o{' '}
            <button
              type="button"
              className="adjuntos__elegir"
              disabled={subiendo || !consulta.data || disponibles <= 0}
              onClick={elegir}
            >
              elígelos desde tu equipo
            </button>
          </div>
          <p className="adjuntos__ayuda">
            Imágenes, PDF, texto, CSV, Word, Excel y PowerPoint. Máximo{' '}
            {formatearTamano(TAMANO_MAXIMO_ADJUNTO)} cada uno; van {cupoTexto}.
          </p>
        </>
      )}

      {subidas.length > 0 && (
        <ul className="subidas" aria-label="Archivos subiendo">
          {subidas.map((subida) => (
            <FilaSubida key={subida.clave} subida={subida} />
          ))}
        </ul>
      )}
      <p className="solo-lectores" role="status">
        {subiendo ? `Subiendo ${subidas.length - pendientes.length + 1} de ${subidas.length}…` : ''}
      </p>
      {problemas.length > 0 && (
        <ul className="adjuntos__problemas" role="alert">
          {problemas.map((problema, i) => (
            // Índice en la clave: dos archivos distintos pueden dar el mismo mensaje.
            <li key={`${i}-${problema}`}>{problema}</li>
          ))}
        </ul>
      )}

      {consulta.isPending && (
        <div aria-busy="true">
          <span className="solo-lectores">Cargando adjuntos…</span>
          <MiniaturasCargando mostrar={mostrarEsqueleto} />
        </div>
      )}
      {consulta.isError && (
        <p className="adjuntos__problemas" role="alert">
          No se pudieron cargar los adjuntos. {mensajeDeErrorAdjunto(consulta.error)}
        </p>
      )}
      {consulta.data && items.length === 0 && <p className="adjuntos__estado">Sin adjuntos.</p>}

      {imagenes.length > 0 && (
        <ul className="adjuntos__fotos">
          {imagenes.map((adjunto) => (
            <li key={adjunto.id} className="adjuntos__foto-item">
              <button
                type="button"
                className="adjuntos__foto"
                title={adjunto.nombre}
                aria-label={`Ver ${adjunto.nombre}`}
                onClick={() => setAbiertoId(adjunto.id)}
              >
                <img src={urlAdjunto(adjunto.id, true)} alt="" loading="lazy" />
                <span className="adjuntos__foto-nombre">{adjunto.nombre}</span>
              </button>
              {puedeQuitar(adjunto) && (
                <BotonQuitar
                  nombre={adjunto.nombre}
                  ocupado={eliminar.isPending}
                  sobreFoto
                  onQuitar={() => quitar(adjunto)}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {documentos.length > 0 && (
        <ul className="adjuntos__docs">
          {documentos.map((adjunto) => (
            <li
              key={adjunto.id}
              className="adjuntos__doc"
              // La fila entera abre el visor con el mouse; con teclado se usa el botón del nombre.
              // Los botones de la fila (descargar, quitar) no lo abren.
              onClick={(evento: MouseEvent<HTMLLIElement>) => {
                if (evento.target instanceof Element && evento.target.closest('a, button')) return;
                setAbiertoId(adjunto.id);
              }}
            >
              <button
                type="button"
                className="adjuntos__doc-abrir"
                title={adjunto.nombre}
                onClick={() => setAbiertoId(adjunto.id)}
              >
                <IconoExtension adjunto={adjunto} />
                <span className="adjuntos__doc-info">
                  <span className="adjuntos__doc-nombre">{adjunto.nombre}</span>
                  <MetaAdjunto adjunto={adjunto} className="adjuntos__meta" />
                </span>
              </button>
              <a
                className="adjuntos__icono-boton"
                href={urlAdjunto(adjunto.id)}
                download={adjunto.nombre}
                aria-label={`Descargar ${adjunto.nombre}`}
                title="Descargar"
              >
                <Icono nombre="bajar" tamano={16} />
              </a>
              {puedeQuitar(adjunto) && (
                <BotonQuitar
                  nombre={adjunto.nombre}
                  ocupado={eliminar.isPending}
                  onQuitar={() => quitar(adjunto)}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {abiertoId !== null && (
        <VisorAdjuntos
          adjuntos={orden}
          actualId={abiertoId}
          onCambiar={setAbiertoId}
          onCerrar={() => setAbiertoId(null)}
        />
      )}
    </section>
  );
}

/**
 * Quitar en dos clics: el primero convierte la papelera en «¿Quitar?» (rojo) y, si no hay un segundo
 * clic en ~2,5 s, vuelve sola a la papelera. El segundo borra.
 */
function BotonQuitar({
  nombre,
  ocupado,
  sobreFoto = false,
  onQuitar,
}: {
  nombre: string;
  ocupado: boolean;
  sobreFoto?: boolean;
  onQuitar: () => void;
}) {
  const [armado, setArmado] = useState(false);
  const temporizador = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(temporizador.current), []);

  const clases = [
    'adjuntos__icono-boton',
    'adjuntos__quitar',
    sobreFoto ? 'adjuntos__quitar--foto' : '',
    armado ? 'adjuntos__quitar--armado' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={clases}
      disabled={ocupado}
      aria-label={armado ? `Confirmar: quitar ${nombre}` : `Quitar ${nombre}`}
      title={armado ? 'Haz clic otra vez para quitarlo' : 'Quitar'}
      onClick={() => {
        window.clearTimeout(temporizador.current);
        if (armado) {
          setArmado(false);
          onQuitar();
          return;
        }
        setArmado(true);
        temporizador.current = window.setTimeout(() => setArmado(false), ESPERA_CONFIRMAR_MS);
      }}
    >
      {armado ? '¿Quitar?' : <Icono nombre="papelera" tamano={16} />}
    </button>
  );
}
